<template>
  <v-dialog
      v-model="show"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      @keydown.esc="close"
  >
    <v-card class="live-editor-wrapper" tile>
      <!-- HEADER -->

      <!-- BODY -->
      <div class="editor-body">
        <!-- LEFT: CANVAS AREA -->
        <div class="canvas-container" ref="canvasRef">
          <v-toolbar color="primary" dark dense elevation="0">
            <v-btn icon dark @click="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title>
              Live Editor - {{ orderName }}
              <span class="text-caption ml-2" style="opacity: 0.7">
                ({{ configMaterial }} / {{ effectiveOrientation }} / {{ configSize }})
              </span>
            </v-toolbar-title>
          </v-toolbar>
          <div class="canvas-inner">
            <div
                @click="() => (dialogImage = currentImageUrl)"
                class="zoom-hint d-flex align-center"
            >
              <v-icon small>mdi-alpha-y-box-outline</v-icon>
              =
              <v-icon small>mdi-loupe</v-icon>
            </div>
            <!-- FRAME -->
            <div
                ref="frameRef"
                class="print-frame elevation-10"
                :class="{ 'print-frame--drag-over': isFrameDragOver }"
                :style="frameContainerStyle"
                @mousedown.prevent="startArtworkDrag"
                @wheel.prevent="onWheel"
                @dragover.prevent="onFrameDragOver"
                @dragenter.prevent="onFrameDragEnter"
                @dragleave.prevent="onFrameDragLeave"
                @drop.prevent="onFrameDrop"
            >
              <!-- Artwork -->
              <!-- Passepartout-Ausschnitt -->
              <div class="artwork-clip" :style="artworkClipStyle">
                <img
                    :key="task.updateKey"
                    v-if="currentImageUrl"
                    :src="currentImageUrl"
                    class="artwork-layer"
                    :style="artworkImageStyle"
                    @load="onImageLoad"
                    draggable="false"
                />
                <div v-else class="loading-placeholder">
                  <v-progress-circular indeterminate color="grey"></v-progress-circular>
                </div>
              </div>

              <!-- Draggable Texts -->
              <div
                  v-for="(element, idx) in localTextElements"
                  :key="'text-' + idx"
                  class="text-layer"
                  :style="getTextStyle(element, idx)"
                  @mousedown.stop.prevent="startTextDrag($event, idx)"
                  @click.stop="onTextClick(idx)"
              >
                <!-- SVG-Wolke hinter dem Text - identisch zur Render-Logik -->
                <svg
                    v-if="element.bgEnabled && textBoxes[idx] && textBoxes[idx].w > 0"
                    class="text-cloud-svg"
                    :width="cloudDims(idx).rx * 2"
                    :height="cloudDims(idx).ry * 2"
                    :viewBox="`0 0 ${cloudDims(idx).rx * 2} ${cloudDims(idx).ry * 2}`"
                >
                  <defs>
                  <radialGradient :id="`cloud-${uid}-${idx}`" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" :stop-color="cloudColor(element)" :stop-opacity="cloudAlpha(element).core" />
                    <stop offset="20%" :stop-color="cloudColor(element)" :stop-opacity="cloudAlpha(element).core" />
                    <stop offset="45%" :stop-color="cloudColor(element)" :stop-opacity="cloudAlpha(element).mid" />
                    <stop offset="75%" :stop-color="cloudColor(element)" :stop-opacity="cloudAlpha(element).mid * 0.4" />
                    <stop offset="100%" :stop-color="cloudColor(element)" :stop-opacity="0" />
                  </radialGradient>
                  <filter :id="`cloud-${uid}-${idx}-blur`" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur :stdDeviation="(frameRect.width || 500) * 0.015" />
                  </filter>
                  </defs>
                  <ellipse
                      :cx="cloudDims(idx).rx"
                      :cy="cloudDims(idx).ry"
                      :rx="cloudDims(idx).rx"
                      :ry="cloudDims(idx).ry"
                      :fill="`url(#cloud-${uid}-${idx})`"
                      :filter="`url(#cloud-${uid}-${idx}-blur)`"
                  />
                </svg>
                <span :ref="'textMeasure' + idx" class="text-measure">{{ element.text }}</span>
              </div>

              <!-- Grid -->
              <div class="grid-lines"></div>
            </div>

            <!-- Floating Controls (unter dem Frame) -->
            <div class="floating-controls elevation-4">
              <div class="d-flex align-center pa-2">
                <v-icon small class="mr-2">mdi-magnify-minus</v-icon>
                <v-slider
                    v-model="zoomProxy"
                    :min="zoomMin"
                    :max="zoomMax"
                    step="0.01"
                    hide-details
                    dense
                    class="mx-2"
                    @input="onSliderZoom"
                ></v-slider>
                <v-icon small class="ml-2">mdi-magnify-plus</v-icon>
                <span class="ml-3 caption font-weight-bold" style="width: 40px">
                  {{ (localCanvas.scale * 100).toFixed(0) }}%
                </span>
              </div>

              <v-divider class="my-1"></v-divider>

              <div class="d-flex justify-space-between pa-1">
                <!-- Versions-Timeline: chronologisch alt → neu (Edits und
                     Refinement zeitstempel-sortiert, z. B. Edit 1 → Refinement
                     → Edit 2); Pfeile zeigen die Entstehungs-Reihenfolge,
                     ganz rechts = neueste Version -->
                <div class="le-version-bar">
                  <template v-for="(item, i) in versionBarItems">
                    <v-icon
                        v-if="item.arrow"
                        :key="'le-varr-' + i"
                        x-small
                        class="le-version-arrow"
                    >mdi-chevron-right</v-icon>
                    <button
                        v-else
                        :key="item.key"
                        type="button"
                        class="le-version-chip"
                        :class="{
                          active: selectedVariant === item.key,
                          disabled: item.disabled,
                          pending: item.pending,
                        }"
                        :title="item.title || ''"
                        :disabled="item.disabled"
                        @click="!item.disabled && (selectedVariant = item.key)"
                        @contextmenu="onVersionChipContextMenu($event, item)"
                    >
                      <v-progress-circular v-if="item.pending" indeterminate size="10" width="2" class="mr-1" />
                      {{ item.label }}
                      <v-icon v-if="item.releasedIcon" x-small class="ml-1" title="Für alle freigegeben">mdi-eye-check-outline</v-icon>
                    </button>
                  </template>
                </div>
                <v-btn @click="downloadArtwork" icon>
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </div>

              <!-- Rechtsklick auf einen Edit: Freigabe für Nutzer ohne AI_EDIT -->
              <v-menu
                  v-model="releaseMenu.show"
                  :position-x="releaseMenu.x"
                  :position-y="releaseMenu.y"
                  absolute
                  offset-y
              >
                <v-list dense>
                  <v-list-item @click="toggleReleaseEdit">
                    <v-list-item-icon class="mr-3">
                      <v-icon small>{{ releaseMenuEdit && releaseMenuEdit.released ? 'mdi-eye-off-outline' : 'mdi-eye-check-outline' }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>
                      {{ releaseMenuEdit && releaseMenuEdit.released ? 'Freigabe zurückziehen' : 'Freigeben (für alle sichtbar)' }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </div>

        <!-- RIGHT: SIDEBAR -->
        <div class="settings-sidebar">
          <div class="pa-4 sidebar-inner">
            <!-- Segment-Tabs: Übersicht (Previews/KI) | Design (Text/Artwork) -->
            <div class="sidebar-tabs">
              <button
                  type="button"
                  class="sidebar-tabs__tab"
                  :class="{ 'sidebar-tabs__tab--active': sidebarTab === 'overview' }"
                  @click="sidebarTab = 'overview'"
              >
                <v-icon x-small>mdi-view-dashboard-outline</v-icon> Übersicht
              </button>
              <button
                  type="button"
                  class="sidebar-tabs__tab"
                  :class="{ 'sidebar-tabs__tab--active': sidebarTab === 'design' }"
                  @click="sidebarTab = 'design'"
              >
                <v-icon x-small>mdi-format-text</v-icon> Text &amp; Artwork
                <!-- KI-Textvorschlag vorhanden: Hinweis-Punkt -->
                <span v-if="aiConfigProposal" class="sidebar-tabs__dot" :title="aiProposalActive ? 'KI-Vorschlag aktiv' : 'KI-Vorschlag verfügbar'"></span>
              </button>
            </div>
            <!-- PREVIEWS -->
            <div class="sidebar-inner-settings sidebar-scroll">
              <div v-show="sidebarTab === 'overview'">
              <div
                  v-if="task.revisionComment"
                  class="preview-box preview-box--warning mb-2"
              >
                <div class="text-caption grey--text mb-1">
                  Revision Comment / {{ task.revisionComment.userName }} /
                  {{ getDateFromNow(task.revisionComment.timestamp) }}
                </div>
                <span>{{ task.revisionComment.comment || task.revisionComment }}</span>
              </div>
              <!-- Order-Kommentar des Kunden: sichtbar + kopierbar + als KI-Prompt nutzbar -->
              <div v-if="task.orderNote" class="preview-box preview-box--note mb-2">
                <div class="text-caption grey--text mb-1 d-flex align-center">
                  <v-icon x-small class="mr-1">mdi-message-text-outline</v-icon>
                  Order-Kommentar
                  <v-spacer />
                  <v-btn x-small icon :title="orderNoteCopied ? 'Kopiert!' : 'Kopieren'" @click="copyOrderNote">
                    <v-icon x-small>{{ orderNoteCopied ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                  </v-btn>
                  <v-btn v-if="canAiEdit" x-small icon title="Als KI-Prompt übernehmen" @click="aiEditPrompt = task.orderNote">
                    <v-icon x-small>mdi-auto-fix</v-icon>
                  </v-btn>
                </div>
                <span class="order-note-text">{{ task.orderNote }}</span>
              </div>

              <!-- Kunden-Feedback (Bewertung): Thread, Default Übersetzung -->
              <div
                  v-if="task.feedback && task.feedback.hasComment"
                  class="preview-box preview-box--note mb-2"
              >
                <div class="text-caption grey--text mb-1 d-flex align-center">
                  <v-icon
                      x-small
                      class="mr-1"
                      :color="task.feedback.satisfied === false ? '#d92c2c' : '#3f9d68'"
                  >
                    {{ task.feedback.satisfied === false ? "mdi-thumb-down" : "mdi-thumb-up" }}
                  </v-icon>
                  Kunden-Feedback
                  <v-spacer />
                  <v-btn
                      v-if="canAiEdit"
                      x-small
                      icon
                      title="Als KI-Prompt übernehmen"
                      @click="aiEditPrompt = latestFeedbackComment"
                  >
                    <v-icon x-small>mdi-auto-fix</v-icon>
                  </v-btn>
                </div>
                <FeedbackThread :feedback="task.feedback" />
              </div>

              <div
                  v-if="customizationData.originalImage && currentImageUrl"
                  class="mt-2 mb-2"
              >
                <button type="button" class="le-compare-btn" @click="openCompareDialog">
                  <v-icon small>mdi-compare</v-icon>
                  <span>Artwork &amp; Original vergleichen</span>
                  <span class="le-compare-btn__key">Y</span>
                </button>
              </div>

              <!-- KI-Änderung: Prompt -> gpt-image-2-Edit -> 4K-Ultra-Upscale -->
              <div v-if="canAiEdit" class="preview-box ai-edit-box mb-2">
                <div class="text-caption grey--text mb-1 d-flex align-center">
                  <v-icon x-small class="mr-1">mdi-auto-fix</v-icon>
                  KI-Änderung
                  <v-spacer />
                  <span v-if="aiEdits.length" class="ai-edit-box__count">{{ aiEdits.length }}</span>
                </div>
                <v-textarea
                    v-model="aiEditPrompt"
                    :disabled="aiEditBusy"
                    rows="2"
                    auto-grow
                    dense
                    hide-details
                    solo
                    flat
                    background-color="#fff"
                    placeholder="Was soll geändert werden? z. B. 'Entferne die Person im Hintergrund'"
                    class="ai-edit-box__input"
                />
                <div v-if="aiEditError" class="ai-edit-box__error">{{ aiEditError }}</div>
                <v-btn
                    color="primary"
                    small
                    block
                    depressed
                    class="mt-2"
                    :loading="aiEditBusy"
                    :disabled="!aiEditPrompt || aiEditPending"
                    @click="runAiEdit"
                >
                  <v-icon left small>mdi-creation</v-icon>
                  Änderung generieren (4K)
                </v-btn>
                <div v-if="aiEditPending" class="text-caption grey--text mt-1 text-center">
                  Bearbeitung läuft im Hintergrund
                </div>
              </div>
              <div class="preview-box mb-2">
                <div class="text-caption grey--text mb-1">Customer Preview</div>
                <v-btn
                    @click="
                  downloadFileFromUrl(
                    customizationData.artworkImageUrl,
                    `${task.orderName}_artwork`
                  )
                "
                    class="preview-box--download-btn"
                    icon
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
                <img
                    @click="() => (dialogImage = customizationData.previewImg)"
                    :src="customizationData.previewImg"
                    class="preview-img preview-img-main"
                    :key="task.updateKey"
                />
              </div>
              <!-- Ersetzt den bisherigen "Original Upload" preview-box-small Block -->

              <div
                  v-if="customizationData.originalImage"
                  class="mb-4"
              >
                <div class="d-flex" style="gap: 8px;">

                  <!-- Original — immer sichtbar -->
                  <div class="preview-box preview-box-small" style="flex:1; min-width:0;">
                    <div class="text-caption grey--text mb-1" style="font-size:10px;">Original</div>
                    <v-btn
                        @click="downloadFileFromUrl(customizationData.originalImage, `${task.orderName}_original`)"
                        class="preview-box--download-btn"
                        icon x-small
                    >
                      <v-icon small>mdi-download</v-icon>
                    </v-btn>
                    <img
                        @click="() => (dialogImage = customizationData.originalImage)"
                        :src="customizationData.originalImage"
                        :key="task.updateKey"
                        class="preview-img preview-img-small"
                    />
                  </div>

                  <!-- Original 2 — nur bei Zwei-Fotos-Kombination -->
                  <div
                      v-if="customizationData.originalImage2"
                      class="preview-box preview-box-small"
                      style="flex:1; min-width:0;"
                  >
                    <div class="text-caption grey--text mb-1" style="font-size:10px;">Original 2</div>
                    <v-btn
                        @click="downloadFileFromUrl(customizationData.originalImage2, `${task.orderName}_original2`)"
                        class="preview-box--download-btn"
                        icon x-small
                    >
                      <v-icon small>mdi-download</v-icon>
                    </v-btn>
                    <img
                        @click="() => (dialogImage = customizationData.originalImage2)"
                        :src="customizationData.originalImage2"
                        :key="task.updateKey"
                        class="preview-img preview-img-small"
                    />
                  </div>

                  <!-- Freepik Upscale ODER Cropped — nur wenn vorhanden -->
                  <div
                      v-if="customizationData.originalUpscaledUrl || customizationData.croppedUrl"
                      class="preview-box preview-box-small"
                      style="flex:1; min-width:0; position:relative;"
                  >
                    <!-- U = Upscaled, C = Cropped -->
                    <v-chip
                        x-small
                        :color="customizationData.originalUpscaledUrl ? 'primary' : 'grey darken-1'"
                        dark
                        style="position:absolute; top:6px; left:6px; z-index:2; font-weight:bold;"
                    >
                      {{ customizationData.originalUpscaledUrl ? 'U' : 'C' }}
                    </v-chip>

                    <div class="text-caption grey--text mb-1" style="font-size:10px;">
                      {{ customizationData.originalUpscaledUrl ? 'Freepik Upscale' : 'Cutout' }}
                    </div>
                    <v-btn
                        @click="downloadFileFromUrl(
            customizationData.originalUpscaledUrl || customizationData.croppedUrl,
            `${task.orderName}_${customizationData.originalUpscaledUrl ? 'upscaled' : 'cropped'}`
          )"
                        class="preview-box--download-btn"
                        icon x-small
                    >
                      <v-icon small>mdi-download</v-icon>
                    </v-btn>
                    <img
                        @click="() => (dialogImage = customizationData.originalUpscaledUrl || customizationData.croppedUrl)"
                        :src="customizationData.originalUpscaledUrl || customizationData.croppedUrl"
                        :key="task.updateKey"
                        class="preview-img preview-img-small"
                    />
                  </div>

                </div>
              </div>

              </div>

              <div v-show="sidebarTab === 'design'">
              <!-- TEXT SETTINGS -->
              <div class="d-flex justify-space-between align-center mb-2">
                <div class="le-section-title"><v-icon small class="mr-1">mdi-format-text</v-icon>Texte</div>
                <div>
                  <v-btn icon small @click="deleteTexts">
                    <v-icon>mdi-delete-forever</v-icon>
                  </v-btn>
                  <v-btn icon small @click="resetTexts">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- KI-Textvorschlag (Auto-Fix nach 👎 + Kommentar): Umschalter
                   Aktuell / KI-Vorschlag — wird NIE automatisch angewendet -->
              <div v-if="aiConfigProposal" class="ai-proposal-box mb-3">
                <div class="ai-proposal-box__head">
                  <v-icon small color="#7b5cd6">mdi-creation</v-icon>
                  KI-Textvorschlag
                </div>
                <div v-if="aiProposalRequest" class="ai-proposal-box__request">
                  „{{ aiProposalRequest }}“
                </div>
                <div class="ai-proposal-toggle">
                  <button
                      type="button"
                      class="ai-proposal-toggle__btn"
                      :class="{ 'ai-proposal-toggle__btn--active': !aiProposalActive }"
                      @click="applyAiProposal(false)"
                  >
                    Aktuell
                  </button>
                  <button
                      type="button"
                      class="ai-proposal-toggle__btn"
                      :class="{ 'ai-proposal-toggle__btn--active': aiProposalActive }"
                      @click="applyAiProposal(true)"
                  >
                    KI-Vorschlag
                  </button>
                </div>
                <div v-if="aiProposalDiff.length" class="ai-proposal-box__diff">
                  <span v-for="(line, i) in aiProposalDiff" :key="i">{{ line }}</span>
                </div>
              </div>

              <v-expansion-panels accordion multiple v-model="openPanels" flat>
                <v-expansion-panel
                    v-for="(element, index) in localTextElements"
                    :key="index"
                    :ref="'textPanel' + index"
                    class="grey lighten-5 mb-2 rounded border-panel"
                >
                  <v-expansion-panel-header class="py-2">
                    Text #{{ index + 1 }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="field-label">Text</div>
                    <v-text-field
                        v-model="element.text"
                        :ref="'textInput' + index"
                        solo
                        flat
                        dense
                        hide-details
                        background-color="#f4f5f7"
                        class="mb-3"
                    ></v-text-field>

                    <v-row dense>
                      <v-col cols="8">
                        <div class="field-label">Schriftart</div>
                        <v-select
                            v-model="element.font"
                            :items="fontOptions"
                            solo
                            flat
                            dense
                            hide-details
                            background-color="#f4f5f7"
                            :menu-props="{ bottom: true, offsetY: true }"
                        >
                          <template v-slot:item="{ item }">
                            <span :style="{ fontFamily: item }">{{ item }}</span>
                          </template>
                        </v-select>
                      </v-col>
                      <v-col cols="4">
                        <div class="field-label">Farbe</div>
                        <div
                            class="color-swatch"
                            :style="{ backgroundColor: element.color }"
                            @click="openColorPicker(index)"
                        >
                          <input
                              type="color"
                              v-model="element.color"
                              :ref="'cp' + index"
                              class="hidden-input"
                          />
                        </div>
                      </v-col>
                    </v-row>

                    <div class="mt-4">
                      <div class="caption grey--text">
                        Size ({{ element.size }}%)
                      </div>
                      <v-slider
                          v-model="element.size"
                          min="1"
                          max="20"
                          step="0.1"
                          dense
                          hide-details
                      ></v-slider>
                    </div>

                    <div class="mt-2">
                      <div class="caption grey--text">
                        Pos Y ({{ element.y }}%)
                      </div>
                      <v-slider
                          v-model="element.y"
                          min="0"
                          max="100"
                          step="0.5"
                          dense
                          hide-details
                      ></v-slider>
                    </div>

                    <!-- Lesbarkeit verbessern -->
                    <div class="mt-3 d-flex align-center justify-space-between">
                      <div class="caption grey--text">Lesbarkeit verbessern</div>
                      <v-switch
                          :input-value="element.bgEnabled"
                          @change="val => onToggleBg(index, val)"
                          dense
                          hide-details
                          class="mt-0 pt-0"
                      ></v-switch>
                    </div>

                    <div v-if="element.bgEnabled" class="mt-2">
                      <div class="caption grey--text">
                        Intensität ({{ ((element.bgIntensity != null ? element.bgIntensity : 0.45) * 100).toFixed(0) }}%)
                      </div>
                      <v-slider
                          v-model="element.bgIntensity"
                          min="0.1"
                          max="0.9"
                          step="0.01"
                          dense
                          hide-details
                      ></v-slider>

                      <!-- Wolkenfarbe: Auto (hell/dunkel je Textfarbe) oder frei wählbar -->
                      <div class="d-flex align-center justify-space-between mt-2">
                        <div class="caption grey--text">Wolkenfarbe</div>
                        <div class="d-flex align-center" style="gap: 6px">
                          <button
                              type="button"
                              class="cloud-auto-chip"
                              :class="{ 'cloud-auto-chip--active': !element.bgColor }"
                              @click="setCloudColor(index, null)"
                          >
                            Auto
                          </button>
                          <div
                              class="color-swatch color-swatch--small"
                              :style="{ backgroundColor: cloudColor(element) }"
                              @click="openCloudColorPicker(index)"
                          >
                            <input
                                type="color"
                                :value="cloudColor(element)"
                                :ref="'cloudcp' + index"
                                class="hidden-input"
                                @input="e => setCloudColor(index, e.target.value)"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-divider class="mt-4 mb-3"></v-divider>
              <!-- ARTWORK SETTINGS -->
              <div class="d-flex justify-space-between align-center mb-2">
                <div class="le-section-title"><v-icon small class="mr-1">mdi-image-outline</v-icon>Artwork</div>
                <v-btn icon small @click="resetArtworkTransform">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </div>

              <v-expansion-panels
                  accordion
                  multiple
                  flat
                  v-model="openArtworkPanels"
              >
                <v-expansion-panel
                    class="grey lighten-5 mb-2 rounded border-panel"
                >
                  <v-expansion-panel-header class="py-2">
                    Drehung / Format
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <!-- Rotation -->
                    <div class="mb-4">
                      <div class="caption grey--text mb-1">
                        Rotation ({{ (localCanvas.rotation || 0).toFixed(0) }}°)
                      </div>
                      <div class="d-flex align-center">
                        <v-btn icon small @click="rotateLeft90">
                          <v-icon small>mdi-rotate-left</v-icon>
                        </v-btn>
                        <v-btn
                            icon
                            small
                            @click="localCanvas.rotation = localCanvas.rotation - 1"
                        >
                          <v-icon small>mdi-minus</v-icon>
                        </v-btn>
                        <v-slider
                            v-model="localCanvas.rotation"
                            :min="-180"
                            :max="180"
                            step="1"
                            dense
                            hide-details
                            class="mx-2"
                        ></v-slider>
                        <v-btn
                            icon
                            small
                            @click="localCanvas.rotation = localCanvas.rotation + 1"
                        >
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn icon small @click="rotateRight90">
                          <v-icon small>mdi-rotate-right</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <!-- Canvas Format -->
                    <div class="mb-4">
                      <div class="caption grey--text mb-1">
                        Canvas-Format
                      </div>
                      <v-btn-toggle
                          v-model="localOrientation"
                          dense
                          rounded
                          mandatory
                          style="display: flex"
                          @change="onOrientationChange"
                      >
                        <v-btn style="flex: 1" value="portrait">Hochformat</v-btn>
                        <v-btn style="flex: 1" value="landscape">Querformat</v-btn>
                      </v-btn-toggle>

                      <!-- KI-Formatwechsel: Illustration selbst umbauen (nicht nur Canvas) -->
                      <v-btn
                          v-if="canAiEdit"
                          small
                          block
                          depressed
                          color="primary"
                          class="mt-2"
                          :loading="aiEditBusy"
                          :disabled="aiEditPending || !currentImageUrl"
                          @click="runAiReformat"
                      >
                        <v-icon left small>mdi-aspect-ratio</v-icon>
                        Mit KI ins {{ localOrientation === "portrait" ? "Querformat" : "Hochformat" }}
                      </v-btn>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel class="grey lighten-5 mb-2 rounded border-panel">
                  <v-expansion-panel-header class="py-2">
                    Passepartout
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <!-- An/Aus -->
                    <div class="d-flex align-center justify-space-between mb-2">
                      <div class="caption grey--text">Passepartout</div>
                      <v-switch
                          :input-value="localCanvas.mat > 0"
                          @change="val => toggleMat(val)"
                          dense
                          hide-details
                          class="mt-0 pt-0"
                      ></v-switch>
                    </div>

                    <!-- Breite -->
                    <div v-if="localCanvas.mat > 0">
                      <div class="caption grey--text mb-1">
                        Breite ({{ (localCanvas.mat || 0).toFixed(0) }}%)
                      </div>
                      <v-slider
                          v-model="localCanvas.mat"
                          :min="matMin"
                          :max="matMax"
                          step="0.5"
                          dense
                          hide-details
                          @input="onMatChange"
                      ></v-slider>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
              </div>
            </div>

            <!-- SEND TO FULFILL (immer sichtbar, sticky unten) -->

            <div class="live-action-container">
              <div class="auto-next-row">
                <span class="auto-next-row__label">
                  <v-icon x-small class="mr-1">mdi-fast-forward-outline</v-icon>
                  Danach nächsten Task öffnen
                </span>
                <v-switch
                    v-model="autoNext"
                    dense
                    hide-details
                    class="mt-0 pt-0"
                    @change="persistAutoNext"
                />
              </div>
              <template v-if="!isRatingView && !isRefinementView">
                <div class="d-flex">
                  <button
                      type="button"
                      class="le-flag-btn mt-2 mr-2"
                      title="Flaggen"
                      @click="() => openRevisionDialog(true)"
                  >
                    <v-icon small color="#fff">mdi-flag</v-icon>
                    <span class="le-flag-btn__label">Flaggen</span>
                  </button>

                  <div class="flex-grow-1 d-flex flex-column">
                    <v-btn
                        block
                        class="mt-2"
                        color="warning"
                        :loading="isSending"
                        :disabled="isSending || !currentImageUrl"
                        @click="openRevisionDialog(false)"
                    >
                      <v-icon left>mdi-shaker-outline</v-icon>
                      Überarbeitung Nötig
                    </v-btn>
                  </div>
                </div>
                <v-divider class="mt-2 mb-2"></v-divider>
              </template>
              <div v-if="!isRefinementView" class="d-flex align-center">
                <!-- Fulfill: links, nur Icon — Label bei Hover; Klick 1 = "Sicher?",
                     Klick 2 (innerhalb 4s) = wirklich fulfillen -->
                <button
                    v-if="!isRatingView"
                    type="button"
                    class="le-fulfill-btn mr-2"
                    :class="{ 'le-fulfill-btn--armed': fulfillArmed }"
                    :disabled="isSending || !currentImageUrl"
                    :title="fulfillArmed ? 'Zum Bestätigen erneut klicken' : 'Fulfill'"
                    @click="onFulfillClick"
                >
                  <v-progress-circular
                      v-if="isSending && fulfillWasConfirmed"
                      indeterminate
                      size="18"
                      width="2"
                      color="#fff"
                  />
                  <v-icon v-else small color="#fff">
                    {{ fulfillArmed ? "mdi-alert-circle-outline" : "mdi-truck-check" }}
                  </v-icon>
                  <span class="le-fulfill-btn__label">
                    {{ fulfillArmed ? "Sicher? Erneut klicken" : "Fulfill" }}
                  </span>
                </button>
                <v-btn
                    class="flex-grow-1"
                    color="primary"
                    :loading="isSending && !fulfillWasConfirmed"
                    :disabled="isSending || !currentImageUrl"
                    @click="() => sendToFulfill(true)"
                >
                  <v-icon left>mdi-truck-check</v-icon>
                  <v-icon left>mdi-mail</v-icon>
                  Preview
                </v-btn>
              </div>
              <template v-if="isRefinementView">
                <v-btn
                    v-if="!isRatingView"
                    block
                    color="success"
                    class="mt-2"
                    :loading="isSending"
                    :disabled="isSending || !localRefinementArtwork"
                    @click="() => approveRefinement()"
                >
                  <v-icon left>mdi-check</v-icon>
                  Upload
                </v-btn>
              </template>
              <div
                  v-if="isRefinementView && !localRefinementArtwork"
                  class="mt-2 red--text text-caption"
              >
                Please Upload Refinement
              </div>
              <div v-if="sendError" class="mt-2 red--text text-caption">
                {{ sendError }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Bild-Vorschau -->
    <ImageDialog
        v-if="dialogImage"
        :show="!!dialogImage"
        :image="dialogImage"
        :onClose="() => (dialogImage = null)"
    ></ImageDialog>

    <!-- Vergleichs-Dialog -->
    <v-dialog v-model="compareDialog" max-width="1200px">
      <v-card class="compare-dialog-card">
        <v-card-title
            class="headline d-flex justify-space-between align-center"
        >
          <span>Vergleich – Artwork / {{ compareOriginals.length > 1 ? "Originale" : "Original" }}</span>
          <div class="d-flex align-center">
            <v-btn icon small @click="resetCompareView">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon small @click="compareDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <div
              class="compare-dialog-container"
              @mousedown.prevent="compareOnMouseDown"
              @mousemove.prevent="compareOnMouseMove"
              @mouseup.prevent="compareOnMouseUp"
              @mouseleave.prevent="compareOnMouseUp"
              @wheel.prevent="compareOnWheel"
          >
            <div class="compare-pane">
              <div class="compare-pane-label">Artwork</div>
              <div class="compare-pane-inner">
                <img
                    v-if="currentImageUrl"
                    :src="currentImageUrl"
                    class="compare-dialog-img"
                    draggable="false"
                    :style="compareTransformStyle"
                />
              </div>
            </div>
            <div
                v-for="(original, index) in compareOriginals"
                :key="'compare-orig-' + index"
                class="compare-pane"
            >
              <div class="compare-pane-label">
                {{ compareOriginals.length > 1 ? "Original " + (index + 1) : "Original" }}
              </div>
              <div class="compare-pane-inner">
                <img
                    :src="original"
                    class="compare-dialog-img"
                    draggable="false"
                    :style="compareTransformStyle"
                />
              </div>
            </div>
          </div>
          <div class="caption grey--text text-center mt-2">
            Scrollrad = Zoom · Mausziehen = Verschieben
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Gewählter KI-Edit ist nicht freigegeben: vor der Überarbeitung fragen -->
    <v-dialog v-model="showReleaseOnRefinementDialog" max-width="470" persistent>
      <v-card>
        <v-card-title>KI-Edit freigeben?</v-card-title>
        <v-card-text>
          Die gewählte Variante ist ein <strong>KI-Edit</strong>, den nur Nutzer
          mit AI_EDIT-Berechtigung sehen. Ohne Freigabe sieht die Person in der
          Überarbeitung dieses Bild nicht.
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="showReleaseOnRefinementDialog = false">Abbrechen</v-btn>
          <v-spacer />
          <v-btn color="primary" depressed @click="releaseAndFinishRevision">
            <v-icon small class="mr-1">mdi-eye-check-outline</v-icon>
            Freigeben &amp; weiter
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Überarbeitung-Dialog -->
    <v-dialog v-model="showRevisionDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline"> Überarbeitung nötig </v-card-title>
        <v-card-text>
          <div class="floating-actions">
            <v-btn
                @click="
                revisionComment =
                  'Remove watercolor stains from Faces/Skin'
              "
                x-small
                outlined
                class="ma-1"
            >
              Remove stains In Face
            </v-btn>
            <v-btn
                @click="revisionComment = 'Optimize Face Details'"
                x-small
                outlined
                class="ma-1"
            >
              Optimize Face Details
            </v-btn>
            <v-btn
                @click="revisionComment = 'Remove Numberplate'"
                x-small
                outlined
                class="ma-1"
            >
              Remove Numberplate
            </v-btn>
          </div>
          <div class="text-field-wrapper">
            <v-textarea
                v-model="revisionComment"
                outlined
                label="Beschreibung"
                rows="4"
            ></v-textarea>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showRevisionDialog = false"> Abbrechen </v-btn>
          <v-btn color="primary" @click="confirmRevision"> Bestätigen </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import ImageDialog from "@/components/ImageDialog.vue";
import FeedbackThread from "@/components/AllRatings/FeedbackThread.vue";
import {
  currentMonthYear,
  downloadFileFromUrl,
  downloadImageFromUrl,
  getDateFromNow,
} from "@/utils";
import { mapActions, mapGetters } from "vuex";
import { doc, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions as fbFunctions } from "../../../fb";
import { permissions } from "@/constants";

// Default-Intensität der Lesbarkeits-Wolke (0..1)
const DEFAULT_BG_INTENSITY = 0.45;

export default {
  name: "LiveEditorDialog",
  components: { ImageDialog, FeedbackThread },
  props: {
    value: Boolean,
    task: Object,
    isRatingView: Boolean,
    isRefinementView: Boolean,
    customizationData: {
      type: Object,
      required: true,
    },
    orderName: String,
    upscaledUrls: {
      type: Object,
      default: () => ({}),
    },
    refinementArtwork: {
      type: String,
      default: null,
    },
    onApprove: {
      type: Function,
      default: null,
    },
    storageUploadFn: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      getDateFromNow,
      isAdminRevision: false,
      showRevisionDialog: false,
      dialogImage: null,

      uid: Math.random().toString(36).slice(2, 9),

      // lokales Custom Artwork (z.B. via Drag & Drop)
      localRefinementArtwork: null,
      isFrameDragOver: false,

      // Kommentar für Überarbeitung
      revisionComment: "",

      // Sidebar-Tab (Übersicht | Text & Artwork)
      sidebarTab: "overview",

      // Auto-ReOpen: nach Abschluss/Edit-Start direkt den nächsten Task öffnen
      autoNext: localStorage.getItem("ohmyLiveEditorAutoNext") !== "false",

      // Fulfill-Bestätigung (Zwei-Klick-Schutz)
      fulfillArmed: false,
      fulfillArmTimer: null,
      fulfillWasConfirmed: false,

      // KI-Änderung (gpt-image-2 + Ultra-Upscale)
      aiEditPrompt: "",
      aiEditBusy: false,
      releaseMenu: { show: false, x: 0, y: 0, index: null },
      showReleaseOnRefinementDialog: false,
      aiEditError: "",
      localAiEdits: [],
      // KI-Textvorschlag (Auto-Fix): Umschalt-Zustand + Backup des Ist-Stands
      aiProposalActive: false,
      aiProposalBackup: null,
      orderNoteCopied: false,

      // Artwork Canvas State
      localCanvas: { x: 0, y: 0, scale: 1, mat: 0, rotation: 0 },
      localTextElements: [],

      // Gemessene Textgrößen für die SVG-Wolke: { [idx]: { w, h } }
      textBoxes: {},

      selectedVariant: "ORIGINAL",
      openPanels: [],
      openArtworkPanels: [],

      // Canvas-Ausrichtung
      localOrientation: null,

      isKeyboardZoom: false,

      // Vergleichs-Dialog
      compareDialog: false,
      compareZoom: 2,
      compareOffset: { x: 0, y: 0 },
      compareDrag: {
        active: false,
        startX: 0,
        startY: 0,
        origX: 0,
        origY: 0,
      },

      matMin: 2,
      matMax: 15,

      // Größen
      canvasRect: { width: 0, height: 0 },
      frameRect: { width: 0, height: 0 },
      imageAspectRatio: 1,

      // Dragging
      dragMode: null,
      dragStart: { x: 0, y: 0 },
      dragStartCanvas: { x: 0, y: 0 },
      dragTextIndex: null,
      dragStartTextPos: { x: 0, y: 0 },
      textWasDragged: false,

      zoomProxy: 1,

      isSending: false,
      sendError: null,

      fontOptions: [
        "WindSong",
        "Playfair Display",
        "Montserrat",
        "Raleway",
        "Dancing Script",
        "Anton",
        "Cinzel",
        "Permanent Marker",
      ],
    };
  },
  computed: {
    ...mapGetters(["currentUser", "userPermissions"]),
    /** KI-Funktionen (Prompt-Box, Formatwechsel) nur mit Berechtigung —
     *  bereits erzeugte Edits sieht jeder */
    canAiEdit() {
      const perms = this.userPermissions || [];
      return perms.includes(permissions.ADMIN) || perms.includes(permissions.AI_EDIT);
    },
    matPx() {
      const base = Math.min(this.frameRect.width || 0, this.frameRect.height || 0);
      return (base * (this.localCanvas.mat || 0)) / 100;
    },
    artworkClipStyle() {
      const matPx = this.matPx;
      return {
        position: "absolute",
        top: `${matPx}px`,
        left: `${matPx}px`,
        width: `calc(100% - ${matPx * 2}px)`,
        height: `calc(100% - ${matPx * 2}px)`,
        overflow: "hidden",
      };
    },
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    // Basis-Konfiguration: bevorzugt savedConfig
    config() {
      return (
          this.customizationData.savedConfig ||
          this.customizationData.configuration ||
          {}
      );
    },

    updatedCustomizationConfigData() {
      return {
        ...this.config,
        orientation: this.effectiveOrientation,
        artworkCanvas: { ...this.localCanvas },
        textElements: this.localTextElements
            ? this.localTextElements.map((el) => ({ ...el }))
            : [],
        // Welche Artwork-Variante der Bearbeitung zugrunde lag — beim
        // erneuten Öffnen wird genau diese wieder vorgewählt (finalTemplate
        // ist ein gerenderter Canvas-Export und matcht keine Varianten-URL)
        sentVariant: this.selectedVariant,
        sentArtworkUrl: this.currentImageUrl || null,
      };
    },

    effectiveRefinementArtwork() {
      return (
          this.localRefinementArtwork ||
          this.customizationData.refinementArtwork ||
          this.refinementArtwork ||
          null
      );
    },

    configMaterial() {
      return this.config.material || "Poster";
    },
    configOrientation() {
      return this.config.orientation || "portrait";
    },
    effectiveOrientation() {
      return this.localOrientation || this.configOrientation;
    },
    configSize() {
      return this.config.size || "M";
    },

    releaseMenuEdit() {
      return this.releaseMenu.index != null ? this.aiEdits[this.releaseMenu.index] : null;
    },
    /** Aktuell gewählter KI-Edit (oder null) */
    selectedAiEdit() {
      const key = String(this.selectedVariant || "");
      if (!key.startsWith("AI_EDIT_")) return null;
      return this.aiEdits[Number(key.slice(8))] || null;
    },
    /** Persistierte + in dieser Session erzeugte KI-Edits (dedupliziert) */
    aiEdits() {
      const persisted = Array.isArray(this.customizationData?.aiEdits)
          ? this.customizationData.aiEdits
          : [];
      const known = new Set(persisted.map((e) => e.url));
      return [...persisted, ...this.localAiEdits.filter((e) => !known.has(e.url))];
    },
    /** Läuft gerade ein KI-Edit? Lokal (dieser Dialog) oder am Task
     *  persistiert (Dialog wurde zwischenzeitlich geschlossen). */
    aiEditPending() {
      return this.aiEditBusy || !!this.customizationData?.aiEditPending;
    },
    pendingPromptTitle() {
      const pending = this.customizationData?.aiEditPending;
      return pending?.prompt ? `Läuft: ${pending.prompt}` : "KI-Edit läuft…";
    },
    /** KI-Textvorschlag aus dem Auto-Fix (feedback.autoEdit) — nur anbieten,
     *  wenn er zur aktuellen Element-Anzahl passt */
    aiConfigProposal() {
      const proposal = this.task?.feedback?.autoEdit?.configProposal;
      if (!Array.isArray(proposal) || !proposal.length) return null;
      return proposal;
    },
    aiProposalRequest() {
      return this.task?.feedback?.autoEdit?.textRequest || "";
    },
    /** Kompakte Diff-Zeilen: was ändert der KI-Vorschlag gegenüber gespeichert? */
    aiProposalDiff() {
      const proposal = this.aiConfigProposal;
      if (!proposal) return [];
      const current = this.config?.textElements || [];
      const lines = [];
      proposal.forEach((p, i) => {
        const c = current[i] || {};
        const parts = [];
        if ((p.text || "") !== (c.text || "")) parts.push(`„${c.text || "—"}“ → „${p.text || "—"}“`);
        if (p.font !== c.font) parts.push(`Font ${p.font}`);
        if (p.color !== c.color) parts.push(`Farbe ${p.color}`);
        if (Number(p.size) !== Number(c.size)) parts.push(`Größe ${c.size}% → ${p.size}%`);
        if (Number(p.y) !== Number(c.y)) parts.push(`Y ${c.y}% → ${p.y}%`);
        if (!!p.bgEnabled !== !!c.bgEnabled) parts.push(`Wolke ${p.bgEnabled ? "an" : "aus"}`);
        if (parts.length) lines.push(`Text #${i + 1}: ${parts.join(" · ")}`);
      });
      return lines;
    },
    /** Neuester Kunden-Kommentar (reine Übersetzung) — für den KI-Prompt */
    latestFeedbackComment() {
      const fb = this.task?.feedback || {};
      const comments = fb.comments;
      if (Array.isArray(comments) && comments.length) {
        const latest = comments[comments.length - 1];
        return latest.translated || latest.original || "";
      }
      return fb.commentOriginal || fb.comment || "";
    },
    /** Alle Original-Fotos (bei Kombi-Artworks beide) */
    compareOriginals() {
      return [
        this.customizationData.originalImage,
        this.customizationData.originalImage2,
      ].filter(Boolean);
    },
    validUpscales() {
      const valid = {};
      for (const [key, val] of Object.entries(this.upscaledUrls || {})) {
        const url = typeof val === "object" && val && val.url ? val.url : val;
        if (url && typeof url === "string" && url.startsWith("http")) {
          valid[key] = url;
        }
      }
      return valid;
    },

    /** Versions-Leiste als Timeline: Basis (Original + Upscales), danach die
     *  Kette Edits↔Refinement chronologisch alt → neu. Edits tragen createdAt;
     *  das Refinement seit 25.08.26 refinementArtworkAt — Legacy-Refinements
     *  ohne Zeitstempel gelten als neueste. Pfeil-Einträge ({arrow:true})
     *  trennen die Ketten-Schritte. */
    versionBarItems() {
      const items = [{ key: "ORIGINAL", label: "Original" }];
      for (const key of Object.keys(this.validUpscales)) {
        items.push({ key, label: this.mapVariantName(key) });
      }

      // Kette chronologisch sortieren (Edit-Indizes bleiben stabil, nur die
      // ANZEIGE-Reihenfolge folgt den Zeitstempeln)
      const chain = [];
      (this.aiEdits || []).forEach((edit, index) => {
        // Edits: ohne AI_EDIT-Berechtigung nur sichtbar, wenn freigegeben
        if (!(this.canAiEdit || edit.released)) return;
        chain.push({
          key: "AI_EDIT_" + index,
          label: edit.name || "Edit " + (index + 1),
          title: edit.prompt,
          editIndex: index,
          releasedIcon: this.canAiEdit && edit.released,
          at: edit.createdAt || 0,
        });
      });
      if (this.effectiveRefinementArtwork || this.isRefinementView) {
        chain.push({
          key: "REFINEMENT",
          label: "Refinement",
          disabled: !this.localRefinementArtwork && !this.effectiveRefinementArtwork,
          at: this.customizationData?.refinementArtworkAt || Number.MAX_SAFE_INTEGER,
        });
      }
      chain.sort((a, b) => a.at - b.at);
      for (const step of chain) items.push({ arrow: true }, step);

      if (this.aiEditPending && this.canAiEdit) {
        items.push({ arrow: true }, {
          key: "__AI_PENDING__",
          label: `Edit ${this.aiEdits.length + 1}…`,
          pending: true,
          disabled: true,
          title: this.pendingPromptTitle,
        });
      }
      return items;
    },

    currentImageUrl() {
      if (this.selectedVariant === "REFINEMENT") {
        return this.effectiveRefinementArtwork;
      }
      if (String(this.selectedVariant).startsWith("AI_EDIT_")) {
        const index = Number(String(this.selectedVariant).slice(8));
        return this.aiEdits[index]?.url || null;
      }
      if (this.selectedVariant === "ORIGINAL") {
        return (
            this.customizationData.artworkImageUrl ||
            this.customizationData.originalImage
        );
      }
      const val = this.upscaledUrls[this.selectedVariant];
      return typeof val === "object" && val ? val.url : val;
    },

    targetAspectRatio() {
      return this.effectiveOrientation === "landscape" ? 3 / 2 : 2 / 3;
    },

    zoomMin() {
      return 1;
    },
    zoomMax() {
      return this.effectiveOrientation === "landscape" ? 4 : 5;
    },

    frameContainerStyle() {
      const ratio = this.targetAspectRatio;
      const cw = this.canvasRect.width;
      const ch = this.canvasRect.height;

      if (!cw || !ch) {
        const h = 70;
        const w = h * ratio;
        return {
          height: `${h}vh`,
          width: `${w}vh`,
        };
      }

      const maxW = cw * 0.8;
      const maxH = ch * 0.8;

      let w = maxW;
      let h = w / ratio;

      if (h > maxH) {
        h = maxH;
        w = h * ratio;
      }

      return {
        width: `${w}px`,
        height: `${h}px`,
      };
    },

    imageLayout() {
      if (!this.frameRect.width || !this.frameRect.height || !this.imageAspectRatio) {
        return null;
      }

      const matPx = this.matPx;
      const innerW = this.frameRect.width - matPx * 2;
      const innerH = this.frameRect.height - matPx * 2;

      const frameRatio = innerW / innerH;
      const imgRatio = this.imageAspectRatio;

      let baseW, baseH;
      if (imgRatio > frameRatio) {
        baseH = innerH;
        baseW = baseH * imgRatio;
      } else {
        baseW = innerW;
        baseH = baseW / imgRatio;
      }

      const currentW = baseW * this.localCanvas.scale;
      const currentH = baseH * this.localCanvas.scale;

      const translateX = this.localCanvas.x * currentW;
      const translateY = this.localCanvas.y * currentH;

      return { baseW, baseH, currentW, currentH, translateX, translateY, matPx, innerW, innerH };
    },

    artworkImageStyle() {
      const layout = this.imageLayout;

      const style = {
        position: "absolute",
        top: "0",
        left: "0",
        transformOrigin: "0 0",              // WICHTIG wie früher
        willChange: "transform",
        pointerEvents: "none",
        userSelect: "none",
      };

      if (!layout) {
        style.width = "100%";
        style.height = "100%";
        style.transform = "none";
        return style;
      }

      style.width = `${layout.baseW}px`;
      style.height = `${layout.baseH}px`;

      const rotation = this.localCanvas.rotation || 0;

      style.transform =
          `translate(${layout.translateX}px, ${layout.translateY}px)` +
          (rotation ? ` rotate(${rotation}deg)` : "") +
          ` scale(${this.localCanvas.scale})`;

      return style;
    },

    compareTransformStyle() {
      return {
        transformOrigin: "50% 50%",
        transform: `translate(${this.compareOffset.x}px, ${this.compareOffset.y}px) scale(${this.compareZoom})`,
      };
    },
  },
  watch: {
    selectedVariant(value) {
      // Format-Edits tragen ihre Orientierung — Canvas automatisch anpassen
      if (String(value).startsWith("AI_EDIT_")) {
        const edit = this.aiEdits[Number(String(value).slice(8))];
        if (edit?.orientation && edit.orientation !== this.localOrientation) {
          this.localOrientation = edit.orientation;
          this.onOrientationChange(edit.orientation);
        }
      }
    },
    value(val) {
      if (val) {
        // Geräte-Einstellung frisch lesen — andere (bereits gemountete)
        // Dialog-Instanzen haben sonst einen veralteten Stand
        this.autoNext = localStorage.getItem("ohmyLiveEditorAutoNext") !== "false";
        this.initData();

        this.$nextTick(() => {
          window.requestAnimationFrame(() => {
            this.updateMeasurements();
            this.$nextTick(() => {
              this.clampCanvas();
              this.measureTextBoxes();
            });
          });
        });
      }
    },
    // Bei jeder Änderung an Text/Font/Size/etc. die Wolkengröße neu messen
    localTextElements: {
      deep: true,
      handler() {
        this.$nextTick(() => this.measureTextBoxes());
      },
    },
  },
  created() {

  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    window.addEventListener("mouseup", this.stopDrag);
    window.addEventListener("mousemove", this.onDrag);

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("mouseup", this.stopDrag);
    window.removeEventListener("mousemove", this.onDrag);

    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);

    if (
        this.localRefinementArtwork &&
        typeof this.localRefinementArtwork === "string" &&
        this.localRefinementArtwork.startsWith("blob:")
    ) {
      try {
        URL.revokeObjectURL(this.localRefinementArtwork);
      } catch (e) {}
    }
  },
  methods: {
    downloadFileFromUrl,
    ...mapActions(["markTask", "importLiveTaskRefinement"]),

    // ===========================================================
    //  LESBARKEITS-WOLKE: gemeinsame Helfer (Vorschau + Canvas)
    // ===========================================================

    // Leitet die Wolkenfarbe automatisch aus der Textfarbe ab:
    // heller Text -> dunkle Wolke, dunkler Text -> helle Wolke,
    // mit leicht durchscheinendem Textfarbton (tint).
    getDefaultBg(color) {
      if (!color) return "#000000";
      const hex = color.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      // Wahrgenommene Helligkeit (YIQ), 0..255
      const luminance = (r * 299 + g * 587 + b * 114) / 1000;

      // Heller Text -> dunkler Hintergrund, dunkler Text -> heller Hintergrund.
      // Reiner Graustufenwert, kein Farbton.
      const target = luminance > 128 ? 0 : 255;

      const toHex = (v) => v.toString(16).padStart(2, "0");
      return `#${toHex(target)}${toHex(target)}${toHex(target)}`;
    },

    toggleMat(val) {
      // An -> Default 5%, Aus -> 0
      this.$set(this.localCanvas, "mat", val ? 5 : 0);
      this.$nextTick(() => {
        this.updateMeasurements();
        this.$nextTick(() => this.clampCanvas());
      });
    },

    onMatChange() {
      // Rand-Änderung verändert den inneren Bereich -> neu clampen
      this.$nextTick(() => {
        this.updateMeasurements();
        this.$nextTick(() => this.clampCanvas());
      });
    },

    // Wolkenfarbe: bevorzugt gespeichertes bgColor, sonst abgeleitet
    cloudColor(el) {
      return el.bgColor || this.getDefaultBg(el.color);
    },

    /** Beim Laden immer mit Auto-Wolkenfarbe starten: eine ggf. aus der
     *  Kunden-Konfiguration geerbte bgColor wird entfernt, sodass der
     *  „Auto"-Modus (hell/dunkel je Textfarbe) standardmäßig aktiv ist.
     *  Ein manueller Farbwunsch kann jederzeit im Editor gesetzt werden. */
    withCloudAutoDefault(elements) {
      return (elements || []).map((el) => ({ ...el, bgColor: null }));
    },

    // Alpha-Werte der Verlaufsstops aus der Intensität
    cloudAlpha(el) {
      const intensity = el.bgIntensity != null ? el.bgIntensity : DEFAULT_BG_INTENSITY;
      return { core: intensity, mid: intensity * 0.9 };
    },

    onToggleBg(index, val) {
      this.$set(this.localTextElements[index], "bgEnabled", val);
      // Beim Aktivieren immer mit der Auto-Logik starten (hell/dunkel je Textfarbe)
      if (val) this.$set(this.localTextElements[index], "bgColor", null);
      // Erst nach dem nächsten Paint messen, sonst sind die Maße noch 0/stale
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          this.measureTextBoxes();
        });
      });
    },

    // Ellipsen-Radien aus der gemessenen Textbox (gleiche Faktoren wie React/SVG)
    cloudDims(idx) {
      const box = this.textBoxes[idx] || { w: 0, h: 0 };
      return {
        rx: (box.w / 2) * 1.35,
        ry: (box.h / 2) * 1.7,
      };
    },

    showCloud(element, idx) {
      // liest textBoxes als Ganzes -> reaktiv
      const box = this.textBoxes[idx]
      return !!(element.bgEnabled && box && box.w > 0)
    },

    // "#rrggbb" + alpha(0..1) -> "rgba(r,g,b,a)" (für Canvas-Gradient)
    hexWithAlpha(hex, alpha) {
      const h = (hex || "#000000").replace("#", "");
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    // Misst die gerenderte Größe jedes Text-Spans für die SVG-Wolke
    async measureTextBoxes() {
      if (!Array.isArray(this.localTextElements)) return;

      await Promise.all(
          this.localTextElements.map(async (el, idx) => {
            if (!el) return;

            // Gezielt die verwendete Font laden (Größe ist für load() egal,
            // aber Format "<size> <family>" ist Pflicht)
            if (el.font && document.fonts && document.fonts.load) {
              try {
                await document.fonts.load(`16px "${el.font}"`);
              } catch (e) {
                // ignorieren – wir messen trotzdem
              }
            }

            const refEl = this.$refs["textMeasure" + idx];
            const node = Array.isArray(refEl) ? refEl[0] : refEl;
            if (node) {
              const r = node.getBoundingClientRect();
              this.$set(this.textBoxes, idx, { w: r.width, h: r.height });
            }
          })
      );
    },
    onOrientationChange() {
      // Nur wenn der User im Editor das Format wechselt,
      // NICHT beim initialen Laden aus der Config.
      this.localCanvas = {
        ...this.localCanvas,
        x: 0,
        y: 0,
        scale: 1,
      };
      this.zoomProxy = 1;

      this.$nextTick(() => {
        this.updateMeasurements();
        this.$nextTick(() => this.clampCanvas());
      });
    },

    onKeyDown(e) {
      if (!this.show) return;
      if (e.key !== "y" && e.key !== "Y") return;

      const tag = e.target.tagName;
      const isInput =
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          e.target.isContentEditable;

      if (isInput) return;
      if (this.dialogImage) return;
      if (this.compareDialog) return (this.compareDialog = false);

      if (this.currentImageUrl) {
        this.isKeyboardZoom = true;
        this.openCompareDialog();
      }
    },

    onKeyUp(e) {
      if (!this.show) return;
      if (e.key !== "y" && e.key !== "Y") return;

      const tag = e.target.tagName;
      const isInput =
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          e.target.isContentEditable;

      if (isInput) return;

      this.isKeyboardZoom = false;
    },

    openRevisionDialog(isAdminRevision) {
      this.isAdminRevision = isAdminRevision;
      this.revisionComment = "";
      this.showRevisionDialog = true;
    },

    initData() {
      const config = this.config || {};
      const canvasCfg = config.artworkCanvas || {};

      let rotation = 0;
      if (typeof canvasCfg.rotation === "number") {
        let r = canvasCfg.rotation;
        r = r % 360;
        if (r > 180) r -= 360;
        if (r < -180) r += 360;
        rotation = r;
      }

      this.localCanvas = {
        x: typeof canvasCfg.x === "number" ? canvasCfg.x : 0,
        y: typeof canvasCfg.y === "number" ? canvasCfg.y : 0,
        scale: typeof canvasCfg.scale === "number" ? canvasCfg.scale : 1,
        mat: typeof canvasCfg.mat === "number" ? canvasCfg.mat : 0,
        rotation,
      };

      this.zoomProxy = this.localCanvas.scale || 1;
      this.localOrientation = config.orientation || this.configOrientation;

      this.localTextElements = config.textElements
          ? this.withCloudAutoDefault(JSON.parse(JSON.stringify(config.textElements)))
          : [];

      // Auto-Fix-Textvorschlag als Default aktivieren — der Umschalter
      // "Aktuell" stellt den gespeicherten Stand mit einem Klick wieder her
      this.aiProposalActive = false;
      this.aiProposalBackup = null;
      if (this.aiConfigProposal) {
        this.applyAiProposal(true);
      }

      this.textBoxes = {};

      this.localRefinementArtwork = null;

      if (this.effectiveRefinementArtwork) {
        this.selectedVariant = "REFINEMENT";
      } else if (this.upscaledUrls["DEFAULT"]) {
        this.selectedVariant = "DEFAULT";
      } else {
        this.selectedVariant = "ORIGINAL";
      }

      // Tatsächlich gesendete Variante wiederherstellen: bevorzugt über die
      // Quell-URL (robust gegen Index-Verschiebungen), sonst über den Key
      const sentResolved = this.resolveVariant(config.sentArtworkUrl, config.sentVariant);
      if (sentResolved) this.selectedVariant = sentResolved;

      // Auto-Fix-Bild-Edit vorwählen — der frische KI-Fix zur Beschwerde
      // gewinnt gegenüber der (beanstandeten) gesendeten Variante
      const autoEditUrl = this.task?.feedback?.autoEdit?.editUrl;
      if (autoEditUrl) {
        const autoEditIndex = this.aiEdits.findIndex(e => e.url === autoEditUrl);
        if (autoEditIndex >= 0 && (this.canAiEdit || this.aiEdits[autoEditIndex].released)) {
          this.selectedVariant = "AI_EDIT_" + autoEditIndex;
        }
      }

      this.openPanels = [];
      this.openArtworkPanels = [];

      this.resetCompareView();
    },

    /** Variante aus gespeicherter Quell-URL bzw. Varianten-Key auflösen —
     *  nur wenn sie am aktuellen Task noch existiert, sonst null */
    resolveVariant(url, variantKey) {
      if (url) {
        const editIndex = this.aiEdits.findIndex((e) => e.url === url);
        if (editIndex >= 0 && (this.canAiEdit || this.aiEdits[editIndex].released)) {
          return "AI_EDIT_" + editIndex;
        }
        if (this.effectiveRefinementArtwork === url) return "REFINEMENT";
        const upscaleEntry = Object.entries(this.validUpscales).find(
            ([, u]) => u === url
        );
        if (upscaleEntry) return upscaleEntry[0];
        if (
            url === this.customizationData.artworkImageUrl ||
            url === this.customizationData.originalImage
        ) {
          return "ORIGINAL";
        }
      }
      if (variantKey) {
        const key = String(variantKey);
        if (key.startsWith("AI_EDIT_")) {
          const keyEdit = this.aiEdits[Number(key.slice(8))];
          if (keyEdit && (this.canAiEdit || keyEdit.released)) return key;
        }
        if (key === "REFINEMENT" && this.effectiveRefinementArtwork) return key;
        if (this.validUpscales[key]) return key;
        if (key === "ORIGINAL") return key;
      }
      return null;
    },

    updateMeasurements() {
      this.measureCanvas();
      this.$nextTick(() => {
        this.measureFrame();
        this.$nextTick(() => this.measureTextBoxes());
      });
    },

    async downloadArtwork() {
      await downloadImageFromUrl(
          this.currentImageUrl,
          `${this.task.orderName}_${this.mapVariantName(this.selectedVariant)}`,
          () => {},
          "jpg"
      );
    },

    measureCanvas() {
      if (this.$refs.canvasRef) {
        const rect = this.$refs.canvasRef.getBoundingClientRect();
        this.canvasRect = { width: rect.width, height: rect.height };
      }
    },

    measureFrame() {
      if (this.$refs.frameRef) {
        const rect = this.$refs.frameRef.getBoundingClientRect();
        this.frameRect = { width: rect.width, height: rect.height };
      }
    },

    onResize() {
      this.updateMeasurements();
    },

    onImageLoad(e) {
      const { naturalWidth, naturalHeight } = e.target;
      if (naturalHeight > 0) {
        this.imageAspectRatio = naturalWidth / naturalHeight;
      }

      if (!this.show) return; // falls v-show den Dialog gerade versteckt hält

      this.$nextTick(() => {
        this.updateMeasurements();
        this.$nextTick(() => this.clampCanvas());
      });
    },


    mapVariantName(key) {
      const map = { DEFAULT: "Upscale", PRO_CAR: "Car", PRO_PERSON: "Person" };
      return map[key] || key;
    },

    getTextStyle(text) {
      const frameW = this.frameRect.width || 500;
      const sizePx = (frameW * text.size) / 100;

      return {
        position: "absolute",
        left: `${text.x}%`,
        top: `${text.y}%`,
        transform: "translate(-50%, -50%)",
        fontFamily: text.font,
        fontSize: `${sizePx}px`,
        color: text.color,
        lineHeight: 1.1,
        whiteSpace: "nowrap",
        cursor: "move",
        pointerEvents: "auto",
        userSelect: "none",
      };
    },

    resetTexts() {
      const cfg = this.config || {};
      this.localTextElements = this.withCloudAutoDefault(
          JSON.parse(JSON.stringify(cfg.textElements || []))
      );
      this.aiProposalActive = false;
      this.aiProposalBackup = null;
    },

    /** KI-Textvorschlag ein-/ausschalten. Beim Einschalten wird der
     *  Ist-Stand gesichert und beim Zurückschalten exakt wiederhergestellt —
     *  ein schlechter Vorschlag kostet so nur einen Klick. */
    applyAiProposal(active) {
      if (active === this.aiProposalActive || !this.aiConfigProposal) return;
      if (active) {
        this.aiProposalBackup = JSON.parse(JSON.stringify(this.localTextElements));
        this.localTextElements = JSON.parse(JSON.stringify(this.aiConfigProposal));
      } else if (this.aiProposalBackup) {
        this.localTextElements = JSON.parse(JSON.stringify(this.aiProposalBackup));
      }
      this.aiProposalActive = active;
    },

    deleteTexts() {
      this.localTextElements = this.localTextElements.map((tEl) => {
        return { ...tEl, text: "" };
      });
    },

    openColorPicker(index) {
      const ref = this.$refs["cp" + index];
      const node = Array.isArray(ref) ? ref[0] : ref;
      if (node && typeof node.click === "function") {
        node.click();
      }
    },

    // --- DRAG ARTWORK ---
    startArtworkDrag(e) {
      if (!this.imageLayout) return;
      if (this.dragMode === "text") return;
      this.dragMode = "artwork";
      this.dragStart = { x: e.clientX, y: e.clientY };
      this.dragStartCanvas = { ...this.localCanvas };
    },

    dragArtwork(e) {
      if (this.dragMode !== "artwork" || !this.imageLayout) return;

      const layout = this.imageLayout;
      const dxPx = e.clientX - this.dragStart.x;
      const dyPx = e.clientY - this.dragStart.y;

      const dXPct = dxPx / layout.currentW;
      const dYPct = dyPx / layout.currentH;

      this.localCanvas.x = this.dragStartCanvas.x + dXPct;
      this.localCanvas.y = this.dragStartCanvas.y + dYPct;

      this.clampCanvas();
    },

    /** Klick auf einen Text im Canvas → Tab „Text & Artwork" öffnen, das
     *  passende Panel aufklappen und das Textfeld fokussieren. Nach einem
     *  echten Verschieben (Drag) unterdrückt, damit Repositionieren nicht
     *  ungewollt in die Sidebar springt. */
    onTextClick(index) {
      if (this.textWasDragged) return;
      this.sidebarTab = "design";
      const alreadyOpen = this.openPanels.includes(index);
      if (!alreadyOpen) {
        this.openPanels = [...this.openPanels, index];
      }

      const resolveRef = (name) => {
        const ref = this.$refs[name + index];
        return Array.isArray(ref) ? ref[0] : ref;
      };
      const scrollToPanel = () => {
        const panel = resolveRef("textPanel");
        const el = panel && panel.$el ? panel.$el : null;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      };
      const focusField = () => {
        const field = resolveRef("textInput");
        // preventScroll: der Browser soll das Scrollen NICHT an sich reißen —
        // die Position bestimmt scrollToPanel()
        if (field && field.focus) field.focus({ preventScroll: true });
      };

      this.$nextTick(() => {
        // Sofort grob hinscrollen (Panel-Header sitzt stabil, auch während sich
        // der Inhalt noch aufklappt) und fokussieren …
        scrollToPanel();
        focusField();
        // … und nach der Aufklapp-Animation (~300 ms) final zentrieren, damit
        // das ganze Panel inkl. Textfeld sauber im Sichtfeld liegt.
        if (!alreadyOpen) {
          setTimeout(scrollToPanel, 320);
        }
      });
    },

    // --- DRAG TEXT ---
    startTextDrag(e, index) {
      if (!this.frameRect.width || !this.localTextElements[index]) return;
      this.dragMode = "text";
      this.textWasDragged = false;
      this.dragTextIndex = index;
      this.dragStart = { x: e.clientX, y: e.clientY };
      this.dragStartTextPos = {
        x: this.localTextElements[index].x,
        y: this.localTextElements[index].y,
      };
    },

    dragText(e) {
      if (this.dragMode !== "text") return;
      const idx = this.dragTextIndex;
      if (idx == null || !this.localTextElements[idx]) return;

      const dxPx = e.clientX - this.dragStart.x;
      const dyPx = e.clientY - this.dragStart.y;

      // Ab ein paar Pixeln als echtes Verschieben werten (unterdrückt den
      // Fokus-Sprung des Klicks danach)
      if (Math.abs(dxPx) > 3 || Math.abs(dyPx) > 3) this.textWasDragged = true;

      const frameW = this.frameRect.width || 1;
      const frameH = this.frameRect.height || 1;

      const dXPct = (dxPx / frameW) * 100;
      const dYPct = (dyPx / frameH) * 100;

      const el = this.localTextElements[idx];
      let newX = this.dragStartTextPos.x + dXPct;
      let newY = this.dragStartTextPos.y + dYPct;

      const SNAP_THRESHOLD = 2;
      if (Math.abs(newX - 50) < SNAP_THRESHOLD) {
        newX = 50;
      }
      if (Math.abs(newY - 50) < SNAP_THRESHOLD) {
        newY = 50;
      }

      el.x = newX;
      el.y = newY;
    },

    onDrag(e) {
      if (!this.dragMode) return;
      if (this.dragMode === "artwork") {
        this.dragArtwork(e);
      } else if (this.dragMode === "text") {
        this.dragText(e);
      }
    },

    stopDrag() {
      this.dragMode = null;
      this.dragTextIndex = null;
    },

    // --- DRAG & DROP CUSTOM IMAGE ---
    onFrameDragOver(e) {
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }
    },
    onFrameDragEnter() {
      this.isFrameDragOver = true;
    },
    onFrameDragLeave(e) {
      if (
          e.currentTarget &&
          e.relatedTarget &&
          e.currentTarget.contains(e.relatedTarget)
      )
        return;
      this.isFrameDragOver = false;
    },
    onFrameDrop(e) {
      this.isFrameDragOver = false;
      const dt = e.dataTransfer;
      if (!dt) return;

      if (dt.files && dt.files.length) {
        const file = Array.from(dt.files).find(
            (f) => f.type && f.type.startsWith("image/")
        );
        if (file) {
          const objectUrl = URL.createObjectURL(file);
          this.applyCustomImage(objectUrl);
          return;
        }
      }

      if (dt.items && dt.items.length) {
        for (let i = 0; i < dt.items.length; i++) {
          const item = dt.items[i];
          if (item.kind === "string" && item.type === "text/uri-list") {
            item.getAsString((url) => {
              if (url) {
                this.applyCustomImage(url.trim());
              }
            });
            return;
          }
        }
      }
    },
    applyCustomImage(url) {
      if (
          this.localRefinementArtwork &&
          typeof this.localRefinementArtwork === "string" &&
          this.localRefinementArtwork.startsWith("blob:")
      ) {
        try {
          URL.revokeObjectURL(this.localRefinementArtwork);
        } catch (e) {}
      }
      this.localRefinementArtwork = url;
      this.selectedVariant = "REFINEMENT";
    },

    clampCanvas() {
      const layout = this.imageLayout;
      if (!layout || !this.frameRect.width || !this.frameRect.height) return;

      const innerW = layout.innerW;
      const innerH = layout.innerH;

      const currentW = layout.currentW;
      const currentH = layout.currentH;

      const minXPct = (innerW - currentW) / currentW;
      const maxXPct = 0;
      const minYPct = (innerH - currentH) / currentH;
      const maxYPct = 0;

      let x = this.localCanvas.x;
      let y = this.localCanvas.y;

      if (x < minXPct) x = minXPct;
      if (x > maxXPct) x = maxXPct;
      if (y < minYPct) y = minYPct;
      if (y > maxYPct) y = maxYPct;

      this.localCanvas.x = x;
      this.localCanvas.y = y;
    },

    // --- ZOOM ---
    handleZoom(newScale, oldScale) {
      if (
          !this.frameRect.width ||
          !this.frameRect.height ||
          !this.imageAspectRatio
      ) return;
      if (newScale === oldScale) return;

      const frameW = this.frameRect.width;
      const frameH = this.frameRect.height;
      const frameRatio = frameW / frameH;
      const imgRatio = this.imageAspectRatio;

      let baseW, baseH;
      if (imgRatio > frameRatio) {
        baseH = frameH;
        baseW = baseH * imgRatio;
      } else {
        baseW = frameW;
        baseH = baseW / imgRatio;
      }

      const currentW = baseW * oldScale;
      const currentH = baseH * oldScale;

      const ratio = newScale / oldScale;

      const currentPixelX = this.localCanvas.x * currentW;
      const currentPixelY = this.localCanvas.y * currentH;

      const newPixelX = currentPixelX * ratio + (frameW / 2) * (1 - ratio);
      const newPixelY = currentPixelY * ratio + (frameH / 2) * (1 - ratio);

      const newW = currentW * ratio;
      const newH = currentH * ratio;

      const newX = newPixelX / newW;
      const newY = newPixelY / newH;

      this.localCanvas = {
        ...this.localCanvas,
        x: newX,
        y: newY,
        scale: newScale,
      };

      this.$nextTick(() => {
        this.clampCanvas();
        this.zoomProxy = this.localCanvas.scale;
      });
    },

    onSliderZoom(value) {
      const v = parseFloat(value);
      const newScale = Math.min(Math.max(v, this.zoomMin), this.zoomMax);
      const oldScale = this.localCanvas.scale || 1;
      this.handleZoom(newScale, oldScale);
    },

    onWheel(e) {
      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      let next = (this.localCanvas.scale || 1) + delta;
      next = Math.min(Math.max(next, this.zoomMin), this.zoomMax);
      this.handleZoom(next, this.localCanvas.scale || 1);
    },

    loadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();

        if (typeof url === "string" && url.startsWith("http")) {
          img.crossOrigin = "anonymous";
        }

        img.onload = () => resolve(img);
        img.onerror = (e) => {
          console.error("Image load error", e);
          reject(e);
        };
        img.src = url;
      });
    },

    canvasToBlob(canvas, type = "image/jpeg", quality = 0.9) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error("Failed to create blob from canvas"));
            },
            type,
            quality
        );
      });
    },

    generateShortId() {
      return Math.random().toString(36).substr(2, 6);
    },

    async uploadToStorage(blob, path) {
      const storage = getStorage();
      const storageRef = ref(storage, path);
      const uploadResponse = await uploadBytes(storageRef, blob);
      return await getDownloadURL(uploadResponse.ref);
    },

    // *** WICHTIG: Render-Logik = Editor-Logik ***
    renderArtworkToCanvas(ctx, img, frameW, frameH) {
      const imgW = img.naturalWidth;
      const imgH = img.naturalHeight;
      if (!imgW || !imgH) return;

      // Passepartout (kürzere Seite, wie im Editor)
      const matPx = (Math.min(frameW, frameH) * (this.localCanvas.mat || 0)) / 100;
      const innerW = frameW - matPx * 2;
      const innerH = frameH - matPx * 2;

      const imgRatio = imgW / imgH;
      const frameRatio = innerW / innerH;

      let baseScale;
      if (imgRatio > frameRatio) {
        baseScale = innerH / imgH;
      } else {
        baseScale = innerW / imgW;
      }

      const scale = this.localCanvas.scale || 1;
      const finalScale = baseScale * scale;

      const scaledW = imgW * finalScale;
      const scaledH = imgH * finalScale;

      // Offsets relativ zum inneren Bereich
      const offsetX = matPx + this.localCanvas.x * scaledW;
      const offsetY = matPx + this.localCanvas.y * scaledH;

      const angleDeg = this.localCanvas.rotation || 0;
      const angleRad = (angleDeg * Math.PI) / 180;

      ctx.save();

      // Auf den inneren Bereich clippen, damit nichts in den Rand läuft
      if (matPx > 0) {
        ctx.beginPath();
        ctx.rect(matPx, matPx, innerW, innerH);
        ctx.clip();
      }

      ctx.translate(offsetX, offsetY);
      if (angleRad !== 0) {
        ctx.rotate(angleRad);
      }
      ctx.scale(finalScale, finalScale);
      ctx.drawImage(img, 0, 0);

      ctx.restore();
    },

    // Zeichnet Texte inkl. Lesbarkeits-Wolke auf den finalen Canvas.
    // Die Wolke nutzt createRadialGradient (Kreis -> per Skalierung Ellipse)
    // und denselben Verlauf/dieselben Faktoren wie die SVG-Vorschau.
    renderTextsToCanvas(ctx, frameW, frameH) {
      if (!Array.isArray(this.localTextElements)) return;

      this.localTextElements.forEach((el) => {
        if (!el || !el.text) return;

        const fontSizePx = (frameW * el.size) / 100;
        const x = (el.x / 100) * frameW;
        const y = (el.y / 100) * frameH;

        ctx.save();
        ctx.font = `${fontSizePx}px ${el.font || "sans-serif"}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // --- Lesbarkeits-Wolke (vor dem Text) ---
        if (el.bgEnabled) {
          const metrics = ctx.measureText(el.text);
          const textW = metrics.width;
          const ascent = metrics.actualBoundingBoxAscent || fontSizePx * 0.7;
          const descent = metrics.actualBoundingBoxDescent || fontSizePx * 0.3;
          const textH = ascent + descent;

          const rx = (textW / 2) * 1.6;
          const ry = (textH / 2) * 2.2;

          if (rx > 0 && ry > 0) {
            const bgColor = this.cloudColor(el);
            const { core, mid } = this.cloudAlpha(el);

            ctx.save();
            // weicher Blur wie in der Vorschau (modern browsers)
            ctx.filter = `blur(${frameW * 0.015}px)`;
            // Kreis (r = ry) horizontal zu Ellipse (rx, ry) skalieren
            ctx.translate(x, y);
            ctx.scale(rx / ry, 1);

            const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, ry);
            grad.addColorStop(0.0, this.hexWithAlpha(bgColor, core));
            grad.addColorStop(0.2, this.hexWithAlpha(bgColor, core));
            grad.addColorStop(0.45, this.hexWithAlpha(bgColor, mid));
            grad.addColorStop(0.75, this.hexWithAlpha(bgColor, mid * 0.4));
            grad.addColorStop(1.0, this.hexWithAlpha(bgColor, 0));

            ctx.fillStyle = grad;
            ctx.beginPath();
            ctx.arc(0, 0, ry, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore(); // setzt filter + transform zurück
          }
        }

        // --- Text darüber ---
        ctx.fillStyle = el.color || "#000000";
        ctx.fillText(el.text, x, y);
        ctx.restore();
      });
    },

    resetArtworkTransform() {
      this.localCanvas.x = 0;
      this.localCanvas.y = 0;
      this.localCanvas.scale = 1;
      this.localCanvas.rotation = 0;
      this.zoomProxy = 1;
      this.$nextTick(() => this.clampCanvas());
    },

    rotateRight90() {
      let angle = this.localCanvas.rotation || 0;
      const step = 90;
      let next = Math.ceil(angle / step) * step;
      if (next === angle) {
        next += step;
      }
      if (next > 180) {
        next = -180;
      }
      this.localCanvas.rotation = next;
    },

    rotateLeft90() {
      let angle = this.localCanvas.rotation || 0;
      const step = 90;
      let prev = Math.floor(angle / step) * step;
      if (prev === angle) {
        prev -= step;
      }
      if (prev < -180) {
        prev = 180;
      }
      this.localCanvas.rotation = prev;
    },

    persistAutoNext(value) {
      localStorage.setItem("ohmyLiveEditorAutoNext", value ? "true" : "false");
    },
    onFulfillClick() {
      if (this.isSending) return;
      if (!this.fulfillArmed) {
        // Klick 1: scharf stellen, nach 4s automatisch entschärfen
        this.fulfillArmed = true;
        clearTimeout(this.fulfillArmTimer);
        this.fulfillArmTimer = setTimeout(() => (this.fulfillArmed = false), 4000);
        return;
      }
      // Klick 2: bestätigt — wirklich fulfillen
      clearTimeout(this.fulfillArmTimer);
      this.fulfillArmed = false;
      this.fulfillWasConfirmed = true;
      this.sendToFulfill(false);
    },
    setCloudColor(index, value) {
      // null = zurück zur Auto-Logik (hell/dunkel je Textfarbe)
      this.$set(this.localTextElements[index], "bgColor", value || null);
    },
    openCloudColorPicker(index) {
      const input = this.$refs["cloudcp" + index];
      const el = Array.isArray(input) ? input[0] : input;
      if (el) el.click();
    },
    async copyOrderNote() {
      try {
        await navigator.clipboard.writeText(this.task.orderNote || "");
        this.orderNoteCopied = true;
        setTimeout(() => (this.orderNoteCopied = false), 1500);
      } catch (e) {
        console.error("Kopieren fehlgeschlagen", e);
      }
    },
    /** Rechtsklick in der Versions-Leiste: Freigabe-Menü nur für Edit-Chips
     *  (auf anderen Chips bleibt das Browser-Kontextmenü) */
    onVersionChipContextMenu(e, item) {
      if (item.editIndex == null) return;
      e.preventDefault();
      this.openReleaseMenu(e, item.editIndex);
    },
    /** Rechtsklick auf einen Edit: Freigabe-Menü (nur mit AI_EDIT-Recht) */
    openReleaseMenu(e, index) {
      if (!this.canAiEdit) return;
      this.releaseMenu = { show: false, x: e.clientX, y: e.clientY, index };
      this.$nextTick(() => (this.releaseMenu.show = true));
    },
    /** Freigabe-Flag am Edit togglen und die komplette Edit-Liste am Task
     *  persistieren (tasks + shopifyTasks) — Nutzer ohne AI_EDIT sehen
     *  ausschließlich freigegebene Edits. */
    async toggleReleaseEdit() {
      const edit = this.releaseMenuEdit;
      if (!edit) return;
      await this.setEditReleased(edit, !edit.released);
    },
    async setEditReleased(edit, released) {
      this.$set(edit, "released", released);
      const arr = this.aiEdits.map((e) => ({ ...e }));
      const uid = String(this.task.uid);
      await Promise.all(
        ["tasks", "shopifyTasks"].map((col) =>
          setDoc(
            doc(db, col, uid),
            { customizationData: { aiEdits: arr } },
            { merge: true }
          ).catch(() => {})
        )
      );
    },
    /** Prompt + upscaltes Artwork + Original(e) -> gpt-image-2 -> 4K.
     *  Ergebnis wird serverseitig am Task persistiert (aiEdits-Array) —
     *  der Dialog darf währenddessen geschlossen werden. */
    async runAiEditCall({ prompt, targetOrientation = null }) {
      this.aiEditBusy = true;
      this.aiEditError = "";
      try {
        // Bearbeitungsgrundlage = die im Editor AUSGEWÄHLTE Version
        // (Refinement/Edit/Upscale/Original) — Fallback aufs Upscale
        const artworkUrl =
            this.currentImageUrl ||
            this.validUpscales.DEFAULT ||
            this.customizationData.artworkImageUrl;
        const editArtworkLive = httpsCallable(fbFunctions, "editArtworkLive", {
          timeout: 540_000, // Function läuft bis zu 9 Min — Client-Default (70s) reicht nicht
        });
        const { data } = await editArtworkLive({
          taskUid: this.task.uid?.toString(),
          prompt,
          artworkUrl,
          originalUrls: this.compareOriginals,
          targetOrientation,
        });
        if (data?.ok && data.edit) {
          this.localAiEdits.push(data.edit);
          this.selectedVariant = "AI_EDIT_" + (this.aiEdits.length - 1);
          // Formatwechsel-Edit: Canvas-Ausrichtung automatisch mitziehen
          if (data.edit.orientation && data.edit.orientation !== this.localOrientation) {
            this.localOrientation = data.edit.orientation;
            this.onOrientationChange(data.edit.orientation);
          }
          return true;
        }
        this.aiEditError = data?.error || "Bearbeitung fehlgeschlagen";
        return false;
      } catch (e) {
        console.error("KI-Änderung fehlgeschlagen:", e);
        this.aiEditError = e.message || "Bearbeitung fehlgeschlagen";
        return false;
      } finally {
        this.aiEditBusy = false;
      }
    },
    async runAiEdit() {
      if (!this.aiEditPrompt || this.aiEditPending) return;
      const prompt = this.aiEditPrompt;
      this.aiEditPrompt = "";
      const call = this.runAiEditCall({ prompt });
      // Nutzer wartet ohnehin nicht auf den Edit — direkt zum nächsten Task
      if (this.autoNext) this.$emit("taskCompleted", { autoNext: true });
      await call;
    },
    /** Illustration per KI ins jeweils andere Format bringen (Outpainting) */
    async runAiReformat() {
      if (this.aiEditPending) return;
      const target = this.localOrientation === "portrait" ? "landscape" : "portrait";
      const prompt =
          `Convert this watercolor artwork to ${target} orientation. ` +
          "Extend the watercolor background, splashes and edges naturally to fill the new canvas. " +
          "Keep the subject, faces, colors, text and painting style exactly unchanged.";
      const call = this.runAiEditCall({ prompt, targetOrientation: target });
      if (this.autoNext) this.$emit("taskCompleted", { autoNext: true });
      await call;
    },
    confirmRevision() {
      // Überarbeitung mit gewähltem, nicht freigegebenem KI-Edit: erst fragen,
      // ob er freigegeben werden soll — sonst sieht der Überarbeiter ohne
      // AI_EDIT-Recht das gewählte Bild nicht
      if (!this.isAdminRevision && this.selectedAiEdit && !this.selectedAiEdit.released) {
        this.showReleaseOnRefinementDialog = true;
        return;
      }
      this.finishRevision();
    },
    async releaseAndFinishRevision() {
      if (this.selectedAiEdit) await this.setEditReleased(this.selectedAiEdit, true);
      this.showReleaseOnRefinementDialog = false;
      this.finishRevision();
    },
    finishRevision() {
      if (this.isAdminRevision) {
        this.markTask({
          uid: this.task.uid,
          revisionComment: this.revisionComment,
        });
        this.showRevisionDialog = false;
      } else {
        this.showRevisionDialog = false;
        this.$emit("refinement", {
          revisionComment: this.revisionComment,
          customOriginalImage: null,
          customFinalImage:
              this.localRefinementArtwork || this.refinementArtwork || null,
          currentConfig: this.updatedCustomizationConfigData,
        });
        this.$emit("approveProcessed");
      }
      this.isAdminRevision = false;
      // Review-Flow: Parent darf den nächsten Task öffnen
      this.$emit("taskCompleted", { autoNext: this.autoNext });
    },

    async urlToBlob(url) {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error("Konnte Refinement-Bild nicht laden.");
      }
      return await res.blob();
    },

    async approveRefinement() {
      try {
        this.isSending = true;
        const fileName = this.getSafeFileName(this.task.orderName);
        const blob = await this.urlToBlob(this.localRefinementArtwork);
        const url = await this.uploadToStorage(
            blob,
            `templates/${currentMonthYear}/refinement/${fileName}`
        );

        await setDoc(
            doc(db, `tasks`, this.task?.uid.toString()),
            {
              status: "REVIEW",
              reviewType: "REFINEMENT",
              editors: {
                REFINEMENT: this.currentUser.uid,
              },
              customizationData: {
                refinementArtwork: url,
                // Zeitstempel für die Neueste-Version-Erkennung (Edits↔Refinement)
                refinementArtworkAt: Date.now(),
                savedConfig: this.updatedCustomizationConfigData,
              },
            },
            { merge: true }
        );
      } catch (e) {
        console.error(e);
        this.sendError = e && e.message ? e.message : String(e);
      } finally {
        this.isSending = false;
      }
    },

    getSafeFileName() {
      const shortId = this.generateShortId();
      const baseName = (this.orderName || "order").toString().trim();
      const safeOrderName =
          baseName
              .replace(/\s+/g, "-")
              .replace(/[^a-zA-Z0-9-_]/g, "")
              .toLowerCase() || "order";
      return `${safeOrderName}-${shortId}.jpg`;
    },

    // SEND TO FULFILL
    async sendToFulfill(sendPreviewCustomer = false) {
      try {
        this.isSending = true;
        this.sendError = null;
        this.$emit("approveProcessed");

        function extractSize(str) {
          if (!str || typeof str !== "string") return null;
          const lastPart = str.split("x")?.[0]?.trim();
          const match = lastPart.match(/^(\d+)/);
          return match ? Number(match[1]) : null;
        }

        const size = extractSize(this.config.size);
        const maxSize = size && size > 45 ? 8000 : 4000;

        const orientation = this.effectiveOrientation || "portrait";
        const ratio = orientation === "landscape" ? 3 / 2 : 2 / 3;

        let width, height;
        if (orientation === "landscape") {
          width = maxSize;
          height = Math.round(width / ratio);
        } else {
          height = maxSize;
          width = Math.round(height * ratio);
        }

        const imgUrl =
            this.currentImageUrl ||
            this.customizationData.artworkImageUrl ||
            this.customizationData.originalImage;

        if (!imgUrl) {
          throw new Error("No artwork image URL available.");
        }

        const img = await this.loadImage(imgUrl);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);

        this.renderArtworkToCanvas(ctx, img, width, height);
        this.renderTextsToCanvas(ctx, width, height);

        const blob = await this.canvasToBlob(canvas, "image/jpeg", 0.9);

        const fileName = this.getSafeFileName(this.task.orderName);
        const path = `templates/${currentMonthYear}/final/${fileName}`;
        const url = await this.uploadToStorage(blob, path);

        let urlRefinement = null;
        if (this.localRefinementArtwork) {
          const blobRefinement = await this.urlToBlob(
              this.localRefinementArtwork
          );
          urlRefinement = await this.uploadToStorage(
              blobRefinement,
              `templates/${currentMonthYear}/refinement/${fileName}`
          );
        }

        this.$emit("approve", {
          finalUrl: url,
          refinementUrl: urlRefinement,
          currentConfig: this.updatedCustomizationConfigData,
          sendPreviewCustomer: sendPreviewCustomer,
        });

        this.$emit("approved", url);
        // Review-Flow: Parent darf den nächsten Task öffnen
        this.$emit("taskCompleted", { autoNext: this.autoNext });
      } catch (err) {
        console.error(err);
        this.sendError = err && err.message ? err.message : String(err);
      } finally {
        this.isSending = false;
      }
    },

    // --- Vergleichs-Dialog ---
    openCompareDialog() {
      this.resetCompareView();
      this.compareDialog = true;
    },
    resetCompareView() {
      this.compareZoom = 2;
      this.compareOffset = { x: 0, y: 0 };
      this.compareDrag = {
        active: false,
        startX: 0,
        startY: 0,
        origX: 0,
        origY: 0,
      };
    },
    compareOnWheel(e) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      let next = this.compareZoom + delta;
      next = Math.min(Math.max(next, 0.5), 10);
      this.compareZoom = next;
    },
    compareOnMouseDown(e) {
      this.compareDrag.active = true;
      this.compareDrag.startX = e.clientX;
      this.compareDrag.startY = e.clientY;
      this.compareDrag.origX = this.compareOffset.x;
      this.compareDrag.origY = this.compareOffset.y;
    },
    compareOnMouseMove(e) {
      if (!this.compareDrag.active) return;
      const dx = e.clientX - this.compareDrag.startX;
      const dy = e.clientY - this.compareDrag.startY;
      this.compareOffset = {
        x: this.compareDrag.origX + dx,
        y: this.compareDrag.origY + dy,
      };
    },
    compareOnMouseUp() {
      this.compareDrag.active = false;
    },

    // --- SAVE / CLOSE ---
    save() {
      this.$emit("save", {
        id: this.customizationData.id,
        configuration: {
          ...this.config,
          artworkCanvas: { ...this.localCanvas },
          textElements: this.localTextElements,
          orientation: this.effectiveOrientation,
        },
        selectedUpscaleVariant: this.selectedVariant,
      });
      this.close();
    },
    close() {
      this.stopDrag();
      this.showRevisionDialog = false;
      this.isFrameDragOver = false;
      this.compareDialog = false;
      this.show = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.text-field-wrapper {
  position: relative;
}

.floating-actions {
  display: flex;
  gap: 6px;
  z-index: 10;
  background: transparent;
}

.live-action-container {
  margin-top: auto;
  margin-bottom: 12px;
  text-align: center;
  padding-top: 8px;
  border-top: 1px solid #eee;
  background: white;
}

.live-editor-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f5f5;
  overflow: hidden;
}

.editor-body {
  display: flex;
  flex: 1;
  height: calc(100% - 48px);
  overflow: hidden;
}

/* LEFT SIDE */
.canvas-container {
  flex: 1;
  background-color: #333;
  position: relative;
  display: flex;
  justify-content: center;
  overflow: hidden;
  flex-direction: column;
  align-items: stretch;
}

.canvas-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.zoom-hint {
  background-color: #ffffff;
  position: absolute;
  top: 65px;
  right: 15px;
  border-radius: 30px;
  padding: 9px;
  display: flex;
  z-index: 10;
  cursor: pointer;
}

.print-frame {
  background-color: white;
  position: relative;
  overflow: hidden;
  cursor: grab;
  flex: 0 0 auto;
}

.print-frame:active {
  cursor: grabbing;
}

.print-frame--drag-over {
  outline: 2px dashed rgba(25, 118, 210, 0.9);
  outline-offset: -4px;
}

.v-btn-toggle {
  button {
    text-transform: none !important;
  }
}

.artwork-layer {
  display: block;
}

/* Grid overlay */
.grid-lines {
  display: none;
  position: absolute;
  inset: 0;
  pointer-events: none;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background-image: linear-gradient(
          to right,
          rgba(0, 0, 0, 1) 1px,
          transparent 1px
  ),
  linear-gradient(to bottom, rgba(0, 0, 0, 1) 1px, transparent 1px);
  background-size: 33.3% 33.3%;
  z-index: 10;
}

/* Floating controls */
/* ===== Radikal-Pass: Settings-Formensprache (flach, grün, Pills) ===== */
.settings-sidebar {
  /* Typo-Grundlinie */
  font-size: 13px;

  /* Boxen-Header links statt zentriert */
  .preview-box {
    text-align: left;

    .text-caption {
      justify-content: flex-start;
      font-size: 11px !important;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: rgba(0, 0, 0, 0.45) !important;
    }
  }

  /* Slider: gefüllter grüner Track, weißer Thumb mit grünem Ring */
  ::v-deep .v-slider__track-fill {
    background: #3f9d68 !important;
  }
  ::v-deep .v-slider__track-background {
    background: #e3e6ea !important;
  }
  ::v-deep .v-slider__thumb {
    width: 16px;
    height: 16px;
    left: -8px;
    background: #fff !important;
    border: 2.5px solid #3f9d68 !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  ::v-deep .v-slider__thumb-container:hover .v-slider__thumb {
    transform: scale(1.1);
  }

  /* Switches: modern inset-artig */
  ::v-deep .v-input--switch__track {
    opacity: 1;
    color: #d3d7de;
  }
  ::v-deep .v-input--switch.v-input--is-label-active .v-input--switch__track {
    color: #3f9d68;
  }
  ::v-deep .v-input--switch.v-input--is-label-active .v-input--switch__thumb {
    color: #fff;
  }

  /* Buttons: Pills ohne Großbuchstaben */
  ::v-deep .v-btn {
    text-transform: none;
    letter-spacing: 0.1px;
    border-radius: 999px;
  }

  /* Panel-Header: kompakt, fett, moderner Chevron-Abstand */
  ::v-deep .v-expansion-panel-header {
    font-size: 13px;
    font-weight: 700;
    min-height: 44px;
  }
  ::v-deep .v-expansion-panel--active .border-panel,
  .border-panel.v-expansion-panel--active {
    border-color: rgba(63, 157, 104, 0.45) !important;
  }

  /* Beschriftungen über Slidern */
  .caption {
    font-size: 11px !important;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(0, 0, 0, 0.45) !important;
  }
}

.field-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
}

.le-section-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: rgba(0, 0, 0, 0.85);
}

/* Fulfill: Icon-Pill links — Label fährt bei Hover aus; Armed = Warnzustand */
/* Versions-Timeline: Chips chronologisch alt → neu, Pfeile dazwischen */
.le-version-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 3px;
  min-width: 0;
}

.le-version-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 11px;
  border: 1px solid #e3e1ea;
  border-radius: 999px;
  background: #f7f6fa;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: rgba(0, 0, 0, 0.58);
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover:not(.disabled):not(.active) {
    background: #efedf6;
    border-color: #d5d0e4;
  }

  &.active {
    background: #edeafa;
    border-color: #7b5cd6;
    color: #6a4fc4;
    box-shadow: 0 1px 5px rgba(123, 92, 214, 0.28);
  }

  &.disabled {
    opacity: 0.45;
    cursor: default;
  }

  &.pending {
    cursor: default;
  }
}

.le-version-arrow {
  color: rgba(0, 0, 0, 0.28) !important;
  margin: 0 -1px;
  flex-shrink: 0;
}

.le-flag-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  min-width: 64px;
  height: 40px;
  padding: 0 11px;
  border: none;
  border-radius: 999px;
  background: #f0a92e;
  color: #fff;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  transition: background 0.15s ease, box-shadow 0.15s ease;
  flex-shrink: 0;

  &:hover {
    box-shadow: 0 2px 8px rgba(240, 169, 46, 0.45);
  }

  &__label {
    max-width: 0;
    opacity: 0;
    font-size: 13px;
    font-weight: 700;
    overflow: hidden;
    transition: max-width 0.2s ease, opacity 0.2s ease, margin-left 0.2s ease;
  }

  &:hover .le-flag-btn__label {
    max-width: 120px;
    opacity: 1;
    margin-left: 8px;
  }
}

.le-fulfill-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center; /* Icon-only: mittig */
  gap: 0;
  min-width: 64px;
  height: 40px;
  padding: 0 11px;
  border: none;
  border-radius: 999px;
  background: #3f9d68;
  color: #fff;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  transition: background 0.15s ease, box-shadow 0.15s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    box-shadow: 0 2px 8px rgba(63, 157, 104, 0.4);
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }

  &__label {
    max-width: 0;
    opacity: 0;
    font-size: 13px;
    font-weight: 700;
    transition: max-width 0.25s ease, opacity 0.2s ease, margin 0.25s ease;
  }

  &:hover:not(:disabled) .le-fulfill-btn__label,
  &--armed .le-fulfill-btn__label {
    max-width: 180px;
    opacity: 1;
    margin-left: 8px;
  }

  &--armed {
    background: #e59b3c;
    animation: le-fulfill-pulse 1s ease-in-out infinite;
  }
}

@keyframes le-fulfill-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(229, 155, 60, 0.5); }
  50% { box-shadow: 0 0 0 6px rgba(229, 155, 60, 0); }
}

/* Vergleichs-Button: ruhige Karte mit Tastatur-Hinweis */
.le-compare-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  border-radius: 12px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: rgba(63, 157, 104, 0.5);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &__key {
    margin-left: auto;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-bottom-width: 2px;
    border-radius: 6px;
    padding: 1px 7px;
    color: rgba(0, 0, 0, 0.5);
  }
}

/* ===== Sidebar-Layout: Tabs oben, scrollender Inhalt, Aktionen sticky ===== */
.sidebar-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.sidebar-scroll {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-width: thin;
}

.sidebar-tabs {
  display: flex;
  gap: 4px;
  background: #eef0f3;
  border-radius: 999px;
  padding: 4px;
  margin-bottom: 14px;
  flex-shrink: 0;

  &__tab {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: none;
    background: transparent;
    border-radius: 999px;
    padding: 8px 10px;
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;
    transition: all 0.15s ease;

    .v-icon {
      color: inherit;
    }

    &--active {
      background: #fff;
      color: rgba(0, 0, 0, 0.85);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
    }
  }

  /* Hinweis-Punkt: KI-Vorschlag vorhanden/aktiv */
  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #7b5cd6;
    box-shadow: 0 0 0 3px rgba(123, 92, 214, 0.2);
    flex-shrink: 0;
  }
}

.live-action-container {
  flex-shrink: 0;
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.07);
  padding-top: 8px;
  margin-top: 8px;
}

.auto-next-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 2px;

  &__label {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(0, 0, 0, 0.45);
  }
}

/* Karten-Look für Boxen & Panels */
.preview-box {
  border-radius: 12px !important;
  border: 1px solid rgba(0, 0, 0, 0.07);
  background: #fafbfc;
}

.border-panel {
  background: #fff !important;
  border: 1px solid rgba(0, 0, 0, 0.07) !important;
  border-radius: 12px !important;
}

.preview-img {
  border-radius: 8px;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.015);
  }
}

.cloud-auto-chip {
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: transparent;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  color: rgba(0, 0, 0, 0.55);
  cursor: pointer;

  &--active {
    background: #e4f5eb;
    border-color: #3f9d68;
    color: #2f7d52;
  }
}

/* Kompound-Selektor: überschreibt sicher die spätere .color-swatch-Regel
   (width:100%/height:40px), die sonst wegen gleicher Spezifität + späterer
   Position gewinnt und das Swatch aus dem Panel schieben würde */
.color-swatch.color-swatch--small {
  width: 26px;
  height: 26px;
  border-radius: 4px; /* Quadrat */
  border: 1px solid rgba(0, 0, 0, 0.15);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}

/* ===== Modernisierungs-Pass: Glas-Optik + weiche Karten ===== */
.preview-box--note {
  background: #fff8e6;
  border: 1px solid #f0dfae;
  border-radius: 12px;
}

.order-note-text {
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}

.ai-edit-box {
  background: #eefaf3;
  border: 1px solid #cdeeda;
  border-radius: 12px;
  text-align: left;

  ::v-deep .v-input {
    width: 100%;
    margin: 0;
  }

  ::v-deep .v-input__slot {
    border-radius: 10px;
    box-shadow: none !important;
    border: 1px solid rgba(0, 0, 0, 0.08);
  }

  ::v-deep textarea {
    font-size: 13px;
    line-height: 1.45;
  }

  &__count {
    background: #3f9d68;
    color: #fff;
    font-size: 10.5px;
    font-weight: 700;
    border-radius: 999px;
    padding: 1px 7px;
  }

  &__input ::v-deep .v-input__slot {
    background: #fff !important;
  }

  &__error {
    color: #b71c1c;
    font-size: 12px;
    margin-top: 6px;
  }
}

/* KI-Textvorschlag (Auto-Fix): Umschalter Aktuell / KI-Vorschlag */
.ai-proposal-box {
  background: #f4f0fd;
  border: 1px solid #e0d6f7;
  border-radius: 12px;
  padding: 12px 14px;

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #7b5cd6;
    margin-bottom: 6px;
  }

  &__request {
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.6);
    font-style: italic;
    margin-bottom: 8px;
  }

  &__diff {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 8px;
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.55);
  }
}

.ai-proposal-toggle {
  display: flex;
  background: #fff;
  border: 1px solid #e0d6f7;
  border-radius: 999px;
  padding: 3px;
  gap: 3px;

  &__btn {
    flex: 1;
    border: none;
    background: transparent;
    border-radius: 999px;
    font-size: 12.5px;
    font-weight: 600;
    padding: 5px 0;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.55);
    transition: background 0.15s ease, color 0.15s ease;

    &--active {
      background: #7b5cd6;
      color: #fff;
    }
  }
}

.floating-controls {
  margin-top: 16px;
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 450px;
  z-index: 100;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

/* RIGHT SIDE */
.settings-sidebar {
  width: 360px;
  background: white;
  border-left: 1px solid #ddd;
  z-index: 20;

  display: flex;
  flex-direction: column;
}

.sidebar-inner {
  min-height: 100%;
  position: relative;

  display: flex;
  flex-direction: column;
  flex: 1;
}

.sidebar-inner-settings {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 16px;
}

.preview-box {
  position: relative;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.preview-box--download-btn {
  position: absolute;
  top: 5px;
  right: 5px;
}

.preview-box--warning {
  background-color: #ffd88d;
}

.preview-img {
  max-width: 100%;
  object-fit: contain;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.preview-img-main {
  max-height: 260px;
}

.preview-box-small {
  background-color: #fafafa;
}

.preview-img-small {
  max-height: 90px;
  box-shadow: none;
}

/* Color picker */
.color-swatch {
  width: 100%;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #ccc;
  cursor: pointer;
  position: relative;
}

.hidden-input {
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.border-panel {
  border: 1px solid #eee !important;
}

/* Text layer */
.text-layer {
  z-index: 20;
  position: relative; /* nötig, damit die SVG-Wolke dahinterliegt */
}

.text-cloud-svg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 0;
  overflow: visible;
}

.text-measure {
  position: relative;
  display: inline-block;
  white-space: nowrap;
  z-index: 1;
}

/* Vergleichs-Dialog */
.compare-dialog-card {
  max-height: 90vh;
}

.compare-dialog-container {
  display: flex;
  flex-direction: row;
  gap: 8px;
  height: 70vh;
  cursor: grab;
}

.compare-dialog-container:active {
  cursor: grabbing;
}

.compare-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0b0b0c;
  border-radius: 12px;
  overflow: hidden;
}

.compare-pane-label {
  padding: 4px 8px;
  font-size: 12px;
  color: #fff;
  background: rgba(0, 0, 0, 0.6);
}

.compare-pane-inner {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.compare-dialog-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
}
</style>